import {
  AccessScope,
  AutoCompleteScope,
  CatalogVisibility,
  CutoffUnit,
  DayOfWeek,
  DiscountSource,
  ExpiryUnit,
  LeaveStatus,
  ProductType,
  StaffStatus,
  TaxMode,
  ValidityStartScope,
  ValidityStart,
  ExpiryLogReason,
  CreditLogReason,
  VoidReason,
} from "./enum";

export interface Business {
  id: string;
  name: string;
  email: string;
  address: string;
  phone_number: string;
  business_type: string;
  photo_url?: string;
  // Optional formal/registered name (e.g. "PT Selaras Bugar Raga"), set by a
  // system admin in Profil Bisnis. Printed on Invoice/Receipt PDFs in place
  // of `name` when present — see purchaseReceiptFromParts.
  legal_name?: string | null;
  // Indonesian tax registration number. Same "official paperwork" bucket as
  // legal_name — sysadmin-set, printed on the Invoice/Receipt PDFs.
  npwp?: string | null;
}

export interface BusinessAdminDto {
  user_id: string;
  full_name: string;
  phone_number: string;
  is_active: boolean;
  roles: string[];
}

export interface BusinessDto extends Business {
  admins: BusinessAdminDto[];
}

// Client-facing policy + display settings (Spott). Shared contract — keep
// field names identical to the server's BusinessSettingsDto and the locked
// client (spott-client) shape.
export interface BusinessSettings {
  cancellation_policy_enabled: boolean;
  // A whole number of CutoffUnit hours or minutes before class start; value
  // and unit are null exactly while the policy is off.
  cancellation_cutoff_value: number | null;
  cancellation_cutoff_unit: CutoffUnit | null;
  /** @deprecated Derived by the server (minutes / 60) for bundles built before
   *  the unit existed; read only as a fallback when the pair is absent. */
  cancellation_cutoff_hours?: number | null;
  // Off: no waitlist at all, and switching it off removed every open spot.
  // Capacity and cutoff are kept and apply again when it is back on.
  waitlist_enabled: boolean;
  waitlist_capacity: number;
  waitlist_auto_promote_cutoff_value: number;
  waitlist_auto_promote_cutoff_unit: CutoffUnit;
  /** @deprecated See cancellation_cutoff_hours. */
  waitlist_auto_promote_cutoff_hours?: number;
  schedule_visibility_days: number;
  // Instructor working hours / leave / class-type assignment enforcement.
  // Master switch; each sub-flag below only takes effect while this is also
  // on. See EmployeeManagementPage and InstructorDetails' conditional tabs.
  employee_management_enabled: boolean;
  employee_management_working_hours_enabled: boolean;
  employee_management_leave_enabled: boolean;
  employee_management_class_assignment_enabled: boolean;
  // Instructor double-booking prevention. Independent of Employee Management
  // (needs no working-hours/leave/class-assignment data) — see
  // ScheduleConflictPolicyPage and assertInstructorAvailable (schedule_v2.ts).
  schedule_conflict_prevention_enabled: boolean;
  // Auto-complete of ended Confirmed bookings (server cron, every 5 min). Default
  // on; a studio that turns it off marks attendance by hand — see
  // AutoCompletePolicyPage. The scope is only read while the flag is on and
  // keeps its stored value while off.
  auto_complete_bookings: boolean;
  auto_complete_scope: AutoCompleteScope;
  // Masa Berlaku Produk: validity starts at the client's first class for the
  // chosen product scope, and a never-used package expires after the deadline.
  // Scope + deadline keep their stored value while the switch is off. See
  // ValidityStartPolicyPage.
  validity_start_first_use: boolean;
  validity_start_scope: ValidityStartScope;
  auto_start_value: number;
  auto_start_unit: ExpiryUnit;
  // Admin notification preferences (Notifikasi feed). The two thresholds
  // keep their stored value while their toggle is off.
  notify_clash: boolean;
  notify_client_booking: boolean;
  notify_client_waitlist: boolean;
  notify_client_cancel: boolean;
  notify_reward_claim: boolean;
  notify_package_expiry: boolean;
  notify_package_expiry_days: number;
  notify_low_credit: boolean;
  notify_low_credit_threshold: number;
  // Client-app WhatsApp contact (WhatsappContactPage): the number behind the
  // client app's WhatsApp buttons. The number keeps its stored value while
  // the switch is off; the client endpoints send none meanwhile.
  whatsapp_enabled: boolean;
  whatsapp_number: string | null;
  // Tax Settings — off by default. tax_rate/tax_mode are only meaningful
  // while tax_enabled is true (kept at their last value while off, same
  // convention as the notification thresholds above).
  tax_enabled: boolean;
  tax_mode: TaxMode;
  tax_rate: number | null;
  tax_label: string;
  // Pembatasan Admin: Manager-configured flags, true = plain Admins may not
  // perform that capability. Read through hooks/useAdminCapabilities; written
  // only from AdminRestrictionsPage (the server refuses them from anyone but a
  // Manager). One field per AdminCapability in shared/utils/capabilities.ts.
  admin_restrict_package_void_expiry: boolean;
  admin_restrict_credit_adjustment: boolean;
  admin_restrict_catalog_pricing: boolean;
  admin_restrict_booking_policy: boolean;
  admin_restrict_promo_reward: boolean;
  admin_restrict_operational_settings: boolean;
  admin_restrict_financial_analytics: boolean;
  admin_restrict_login_credentials_client: boolean;
  admin_restrict_login_credentials_instructor: boolean;
  admin_restrict_login_link_regenerate: boolean;
  admin_restrict_booking_refund: boolean;
}

// Per-card saves send only their own fields, so the update payload is partial.
export type UpdateBusinessPoliciesForm = Partial<BusinessSettings>;

/** One changed setting from one save (GET /v2/business/settings/log). Rows of
 *  one save share a batch_id; values are the stored scalars (a null setting
 *  arrives as JSON null). */
export interface BusinessSettingLogEntry {
  id: string;
  batch_id: string;
  field: keyof BusinessSettings | string;
  old_value: boolean | number | string | null;
  new_value: boolean | number | string | null;
  created_at: string;
  actor_name: string;
}

/** A page of changed-field rows, newest first; `total` counts rows. */
export interface BusinessSettingLogPage {
  entries: BusinessSettingLogEntry[];
  total: number;
  page: number;
  page_size: number;
}

// Structured render data for a clash-digest notification. When present, the
// panel renders the chip + session-row layout instead of the HTML content.
export interface ClashNotificationPayload {
  date: string;
  groups: {
    instructors: string[];
    customers: string[];
    sessions: { name: string; time: string }[];
  }[];
}

export interface Notification {
  id: string;
  title: string;
  content: string;
  link?: string;
  seen: boolean;
  created_at: string;
  relative_created_at: string;
  business_id: string;
  avatar?: string;
  // Event category driving the panel icon + filter pills. Null/absent on legacy
  // rows (rendered as a clash). e.g. "clash" | "booked" | "waitlisted" |
  // "cancelled" | "no_show" | "left_waitlist".
  type?: string;
  // Structured render data: clash digests carry `groups`; customer booking
  // events (booked / waitlisted / cancelled / no_show / left_waitlist) carry
  // `kind: "customer_booking"`. Absent on legacy rows, which render from
  // `content`.
  payload?: ClashNotificationPayload | CustomerBookingNotificationPayload;
}

export interface CustomerBookingNotificationPayload {
  kind: "customer_booking";
  class_name: string;
  occurrence_timestamp: string;
  outcome:
    | { type: "none" }
    | { type: "slot_open" }
    | { type: "queued"; waitlist_count: number }
    | { type: "promoted"; name: string }
    // A private session the INSTRUCTOR created or cancelled from the
    // instructor app; `name` is the instructor's.
    | { type: "instructor_booked"; name: string }
    | { type: "instructor_cancelled"; name: string };
}

export interface User {
  id: string;
  full_name: string;
  // Structured name parts, shared across every role on this account
  // (Customer/Staff/Instructor use them; Admin/Manager/SystemAdmin stay on
  // full_name only). null for legacy accounts until backfilled/edited.
  // full_name stays the always-populated composed display value.
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  email: string;
  // Null for a non-teaching Staf who was added without one (they never log
  // in); every account that signs in somewhere always has a phone.
  phone_number: string | null;
  dob?: Date;
  gender?: string;
  roles: string[];
  photo_url?: string;
  customer?: Customer;
  staff?: Staff;
  is_verified: Boolean;
  // Derived by getUserById (never the hash): whether an admin-tier grant in
  // the system admin's role editor must also set a password.
  has_password?: boolean;
  created_at: Date;
  deleted_at?: Date;
}

export interface Customer {
  user_id: string;
  user: User;
  customer_package: CustomerPackage[];
  deleted_at?: Date;
}

export interface Staff {
  user_id: string;
  user: User;
  status: StaffStatus;
  // Jabatan: free-text job title for a non-teaching Staf, display only.
  job_title?: string | null;
  // The instructor's calendar colour, a key of calendarPalette
  // (shared/theme/color.ts). Null only before the server has given one.
  calendar_color?: string | null;
  working_hour: WorkingHour[];
  // Distinguishes "never configured" (false, fully unrestricted) from
  // "explicitly configured, possibly with zero working_hour rows" (true) —
  // an empty working_hour array is valid under either state, so this flag
  // is what tells them apart. See the working_hours_configured gotcha.
  working_hours_configured: boolean;
  leave: Leave[];
  class_types: StaffClassType[];
  // True when this instructor is assigned to every class type rather than
  // an explicit list — mutually exclusive with class_types being non-empty.
  assigned_all_class_types: boolean;
  // Point-in-time snapshots of this instructor's real settings, captured the
  // moment Jam Kerja / Kelas yang Ditugaskan enforcement was last turned
  // back on business-wide (see updateBusinessPolicies) — null means nothing
  // is currently archived. Powers the "Pulihkan pengaturan sebelumnya"
  // button on InstructorWorkingHours.tsx / InstructorClassTypes.tsx.
  archived_working_hours: ArchivedWorkingHourBlock[] | null;
  archived_class_assignment: ArchivedClassAssignment | null;
  // Present only when the fetch was made with the roster-summary flag (the
  // Manajemen Karyawan hub); absent on every other staff fetch.
  employee_management_summary?: EmployeeManagementSummary;
  deleted_at?: Date;
}

// Shape of staff.archived_working_hours — deliberately NOT the same as
// WorkingHour (no `id`; the archive is a plain snapshot, not live rows).
export interface ArchivedWorkingHourBlock {
  day: DayOfWeek;
  start_time: string;
  end_time: string;
}

export interface ArchivedClassAssignment {
  class_type_ids: string[];
}

// One independent working block. A day can have more than one (e.g. a
// 9-11am class and a separate 6-9pm class) — no is_working flag, a day with
// zero blocks simply isn't worked.
export interface WorkingHour {
  id: string;
  day: DayOfWeek;
  start_time: string;
  end_time: string;
}

export interface EmployeeManagementSummary {
  workingDaysCount: number;
  upcomingLeaveCount: number;
  assignedClassCount: number;
  // When true, assignedClassCount is meaningless (can't count "all") — the
  // roster badge must check this first, not the count.
  assignedAllClassTypes: boolean;
}

// Leave has no financial stake (unlike booking_v2, which gets a full
// booking_v2_activity_log because its status transitions govern credits/
// refunds), so a last-actor stamp is enough transparency rather than a
// per-transition history — see TimeOffPage.tsx's "last changed by" note.
export interface Leave {
  id: string;
  start_date: Date;
  end_date: Date;
  status: LeaveStatus;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
  updated_by_name: string | null;
}

// Read-only "what would this affect" preview for a proposed (not-yet-saved)
// leave application. Powers the Ajukan Cuti confirm-before-submit warning.
// Includes EVERY overlapping, non-deleted schedule occurrence regardless of
// booking count — booked_customer_count: 0 means the class is genuinely
// scheduled but nobody's booked yet, still worth warning about.
// booked_customer_names has no phone numbers (dropped per user decision);
// the modal shows names for private/duet/triplet classes (a handful of
// participants max) and collapses to booked_customer_count for open classes.
// Field names must stay identical to the server's own independently-defined
// LeaveConflictPreviewDto (POST /v1/staff/leave/preview,
// previewLeaveConflicts in staff.ts), kept in sync by convention only, same
// as InstructorAvailability/InstructorAvailabilityDto.
export interface AffectedOccurrence {
  schedule_id: string;
  occurrence_timestamp: string;
  class_name: string;
  class_type: string;
  duration: number;
  booked_customer_count: number;
  booked_customer_names: string[];
  // Leave preview only (optional because the working-hours and class-type
  // previews share this shape and do not send them). has_started: the
  // occurrence's start is in the past, so "Hapus Jadwal & Ajukan Cuti" skips
  // it. credit_return_count: bookings that get credit back if it is deleted
  // (wider than booked_customer_count: NoShow and live waitlist holds too).
  has_started?: boolean;
  credit_return_count?: number;
  // Working-hours / class-assignment previews only (RuleConflictWarningModal):
  // "series" = "Hapus Kelas & Simpan" cuts this schedule from this session to
  // the end of its series; "occurrence" = only this session is deleted.
  is_recurring?: boolean;
  deletion_mode?: "series" | "occurrence";
}

// What the server did when working hours / class types were saved with
// remove_affected_schedules: true (carried in the response `meta`).
export interface RuleRemovalResult {
  removed_series: number;
  removed_occurrences: number;
  refunded_bookings: number;
  skipped_started: number;
}

// What the server did when a leave was applied or restored with
// remove_affected_schedules: true (applyLeaveById / restoreLeaveById).
export interface LeaveRemovalResult {
  removed_schedules: number;
  refunded_bookings: number;
  skipped_started: number;
}

export interface LeaveConflictPreview {
  affected_occurrences: AffectedOccurrence[];
}

// Same AffectedOccurrence shape as leave's preview above, for a PROPOSED
// working-hours save (POST /v1/staff/working-hours/preview,
// previewWorkingHoursConflicts) and a PROPOSED class-type assignment save
// (POST /v1/staff/class-types/preview, previewClassAssignmentConflicts).
// Kept as their own named types (not reusing LeaveConflictPreview directly)
// to match the server's own separately-named-but-identically-shaped DTOs.
export interface WorkingHoursConflictPreview {
  affected_occurrences: AffectedOccurrence[];
}

export interface ClassAssignmentConflictPreview {
  affected_occurrences: AffectedOccurrence[];
}

// Read-only, business-wide "what would re-enabling leave enforcement
// immediately flag" scan. Powers a confirm-before-save warning on the
// Employee Management settings page when saving would newly turn on leave
// enforcement (the master flag, the leave sub-flag, or both moving from
// not-enforced to enforced) — schedules created while enforcement was off
// can silently violate an instructor's approved leave the moment it's turned
// back on, and nothing else catches that. Working-hours no longer routes
// through this scan (EmployeeManagementPage.tsx only ever adds "leave" to
// its relevantReasons set): a working-hours off->on transition instead
// triggers the deterministic archive-and-reset flow (see
// EmployeeManagementResetNoticeModal.tsx / ArchivedWorkingHourBlock). The
// "working_hours" reason value is kept in the type below only because a
// single occurrence can still legitimately carry both reasons when leave
// enforcement is what's newly turning on. Field names must stay identical to
// the server's own independently-defined
// EmployeeManagementReactivationPreviewDto (POST /v1/staff/employee-management/
// reactivation-preview, previewEmployeeManagementReactivation in staff.ts).
export interface AffectedInstructorOccurrence {
  instructor_id: string;
  instructor_name: string;
  schedule_id: string;
  occurrence_timestamp: string;
  class_name: string;
  class_type: string;
  duration: number;
  // Both checked independently server-side — an occurrence violating both
  // working hours AND leave carries BOTH here, not just one.
  reasons: ("working_hours" | "leave")[];
}

export interface EmployeeManagementReactivationPreview {
  affected: AffectedInstructorOccurrence[];
}

// A class type an instructor is assigned/qualified to teach. When
// employee_management_enabled is on, the schedule instructor picker is
// hard-filtered to instructors assigned to the class type being scheduled.
export interface StaffClassType {
  id: string;
  name: string;
  type: string;
}

export interface ClassType {
  id: string;
  name: string;
  type: string;
  description: string;
  max_participants: number;
  duration: number;
  // Credits one booking of this class takes, on every package that covers
  // it. Absent from an older server (= 1).
  credit_cost?: number;
  packages: Package[];
}

export interface Package {
  id: string;
  name: string;
  description: string;
  price: number;
  total_credits: number;
  expiry_duration_by_days: number;
  class_type: ClassType[];
  unlimited: boolean;
  // Tier fields (package-products restructure); null on ungrouped legacy rows.
  product_id?: string | null;
  expiry_value?: number | null;
  expiry_unit?: ExpiryUnit | null;
  // Hidden system tier marker (loyalty/referral reward backing rows). Normal
  // packages carry null/undefined; catalog endpoints never return system rows,
  // but customer_package listings DO (the reward-credit badge keys off this).
  system_kind?: string | null;
  system_scope?: string | null;
  // Package sharing, set per tier. max_members counts the OWNER.
  is_shareable?: boolean;
  max_members?: number;
}

// A credit tier (packages row) as returned inside a PackageProduct.
export interface PackageTier {
  id: number;
  name: string;
  price: number;
  total_credits: number;
  unlimited: boolean;
  expiry_value: number | null;
  expiry_unit: ExpiryUnit | null;
  expiry_duration_by_days: number;
  // All-time purchases of this tier (drives the remove-tier warning).
  sold_count: number;
  // Package sharing. max_members counts the OWNER, so 2 means the buyer plus
  // one other person.
  is_shareable: boolean;
  max_members: number;
}

// Catalog product (package_product row + live-tier aggregates) from /v1/product.
export interface PackageProduct {
  id: string;
  name: string;
  description: string | null;
  product_type: ProductType;
  is_promo: boolean;
  promo_starts_at: string | null;
  promo_ends_at: string | null;
  promo_active: boolean;
  // Once its own promo_ends_at has passed — permanently, even if promo_active
  // later reads false for other reasons too. Drives the "locked except name"
  // edit restriction on the product detail page.
  promo_ended: boolean;
  access_scope: AccessScope | null;
  // Storefront listing in the customer app. Hidden products still sell at
  // the front desk, upgrade, and count in analytics.
  client_catalog_visibility: CatalogVisibility;
  business_id: string;
  created_at: string;
  tier_count: number;
  credit_min: number | null;
  credit_max: number | null;
  price_min: number | null;
  price_max: number | null;
  // credit_cost: credits ONE booking of the class takes on every tier of
  // the product (1 unless the studio set otherwise). Absent from an older
  // server, which reads as 1.
  class_type: (Pick<ClassType, "id" | "name" | "type"> & { credit_cost?: number })[];
  tiers: PackageTier[];
  // Membership products only.
  /** Memberships only: live memberships sold (not people; a shared one is one). */
  active_member_count?: number;
  /** Of those, the ones ending inside expiring_soon_days. */
  expiring_soon_count?: number;
  /** The studio's Akan Kedaluwarsa window in days (its expiry alert setting). */
  expiring_soon_days?: number;
  /** The studio's Kredit Akan Habis threshold (its low-credit alert setting). */
  low_credit_threshold?: number;
  // Detail endpoint only: people (owners and co-sharers) who can still book
  // with one of this product's packages. The save warns with it before a
  // class is removed.
  active_holder_count?: number;
}

export interface PackageProductList {
  products: PackageProduct[];
  // Live legacy packages not yet grouped under a product (transition banner).
  ungrouped_count: number;
}

// One live package of a product (GET /v1/product/:id/members), under its
// owner, with the active co-sharers it is shared with. Both product types.
// The fields after `unactivated` came with the product page's Paket Aktif /
// Membership Aktif tab; an older server omits them.
export interface PackageProductMember {
  customer_package_id: string;
  /** The owner. */
  customer_id: string;
  customer_name: string;
  purchase_date: string;
  expiry_date: string | null;
  expiring_soon: boolean;
  /** Masa Berlaku Produk: the clock has not started, so there is no end date. */
  unactivated?: boolean;
  auto_start_date?: string | null;
  phone_number?: string | null;
  /** False when the owner is deactivated at this studio. */
  client_active?: boolean;
  /** The active co-sharers (never the owner). */
  shared_with?: { customer_id: string; name: string; phone_number: string | null }[];
  /** The tier's credit count ("10 sesi"); null on a membership. */
  tier_credits?: number | null;
  total_credits?: number | null;
  remaining_credits?: number | null;
  onhold_credits?: number;
  /** The Kredit Akan Habis rule, at the studio's alert threshold. */
  low_credit?: boolean;
}

export enum InactiveReason {
  NO_MORE_SESSIONS = "NO_MORE_SESSIONS",
  EXPIRED = "EXPIRED",
  MANUALLY_MARKED_INVALID = "MANUALLY_MARKED_INVALID",
  UPGRADED = "UPGRADED",
  // A shared package the client was revoked from. Their bookings and held
  // credits survive the revocation, so the package stays visible on their
  // Paket tab as inactive history rather than vanishing and leaving those
  // bookings unexplained. Display only: it never grants access back.
  ACCESS_REMOVED = "ACCESS_REMOVED",
}

export interface CustomerPackage {
  id: string;
  customer_id: string;
  package_id: string;
  remaining_credits: number;
  onhold_credits: number;
  total_credits: number;
  purchase_date: Date;
  expiry_date?: Date;
  // Validity start (Masa Berlaku Produk). On a "first_use" row whose
  // activated_at is null the expiry_date above IS the activation deadline;
  // once a booking anchors it, expiry = that class's day + tier validity.
  // activation_locked = an admin set the expiry by hand (counts as activated).
  // auto_start_date = the day the clock starts on its own if no class comes
  // first; "belum aktif" while it is still ahead (packageActivation()).
  validity_start?: ValidityStart;
  activated_at?: string | null;
  activation_locked?: boolean;
  auto_start_date?: string | null;
  // The tier's validity length when this package was SOLD. Read through
  // packageValidity(): editing the tier later never changes it.
  validity_value?: number | null;
  validity_unit?: string | null;
  purchase_price: number;
  /**
   * Set only when this row was CREATED BY an upgrade: the difference the client
   * actually paid, where purchase_price is the new package's full nominal
   * value. Read it through amountPaid() rather than directly, so every surface
   * agrees. null/absent on an ordinary purchase.
   */
  upgrade_fee?: number | null;
  /**
   * Set only on a row created by an upgrade that recorded it: the OLD
   * package's value the fee was priced against. When it exceeds
   * purchase_price the fee was floored at 0 and the documents state the
   * difference that is not refunded. Null on older upgrades.
   */
  upgrade_previous_price?: number | null;
  package_deal: Package;
  is_valid: boolean;
  booking: BookingDto[];
  inactive_reason?: InactiveReason;
  /** When a revoked co-sharer lost access. Only set on ACCESS_REMOVED rows. */
  access_removed_at?: string | null;
  // Raw FK — needed to preselect the current value in
  // UpdateCustomerPackagePaymentMethodModal; null when no payment method was
  // recorded.
  payment_method_id: string | null;
  // Flattened server-side from the optional payment_method relation — null
  // when no payment method was recorded (including every purchase made
  // before this feature existed).
  payment_method_name: string | null;
  // Per-business sequential number assigned atomically at purchase time
  // (business.next_transaction_number). Null only for rows that predate the
  // backfill script (src/scripts/backfill-transaction-numbers.ts on the
  // server) not yet having been run against that environment.
  transaction_number: number | null;
  // The purchase's discount, whatever gave it (a code, a loyalty reward or a
  // referral reward), flattened server-side (util/purchase-discount.ts), all
  // null when no discount applied. DOCUMENT basis: the amount as applied,
  // the way the client saw it (invoice, receipt, Transaksi tab). Pembelian's
  // SalesPackagesRow carries the same discount BEFORE TAX instead, so on an
  // inclusive-tax studio the two figures differ by the tax share on purpose.
  /** Absent from an older server, which only ever sent code discounts. */
  discount_source?: DiscountSource | null;
  discount_code: string | null;
  discount_amount: number | null;
  original_price: number | null;
  /** A loyalty/referral reward's percentage; null for a fixed amount or a code. */
  discount_percent?: number | null;
  // Snapshot of the business's tax settings at the moment THIS row was
  // created — never the business's current settings — so the Invoice/
  // Receipt PDF always reflects what was true at purchase time. Null/false
  // for every purchase made before this feature existed.
  tax_enabled_snapshot: boolean;
  tax_mode_snapshot: TaxMode | null;
  tax_rate_snapshot: number | null;
  tax_label_snapshot: string | null;
  // Package sharing. The OWNER stays customer_id; these describe who else may
  // spend from this one purchase. Present on the detail fetch and on the
  // per-customer list; absent elsewhere, hence optional.
  owner_name?: string | null;
  // Detail fetch only. owner_active: the owner is still an active client of
  // this studio. in_use: someone active can still use the package (the owner,
  // or a co-sharer); false means read-only until the owner is reactivated.
  // Absent from an older server, which reads as unlocked.
  owner_active?: boolean;
  in_use?: boolean;
  // True on a row surfaced because it was shared WITH the customer being
  // viewed, rather than bought by them.
  is_shared_with_me?: boolean;
  members?: CustomerPackageMember[];
  shared_with?: { user_id: string; full_name: string | null }[];
  access_holder_count?: number;
  // Detail fetch only: the latest class the package still has to pay for (a
  // seat, a pending request or a waitlist spot). The end-date editor offers
  // no day before it; the server refuses one. Null when nothing is booked.
  last_booked_class_at?: string | null;
  // Credits above what the paid pool still has room for (detail only):
  // classes booked on them earn Rp 0. Absent from an older server.
  goodwill_credits?: number;
  // Detail fetch only: the referral this sale converted, named in the
  // delete pop-up (System Review R2). Null when it converted none.
  referral_conversion?: { referrer_name: string } | null;
  // Booking pickers only (getCustomerPackageByGroup / ByClassType): the days
  // one new class may fall on and be charged to this package, Jakarta
  // YYYY-MM-DD, mirroring the server's window check. On a first-use package
  // whose clock can still move, `anchor` is its earliest possible anchor: a
  // series starting before it opens its own window from its start.
  booking_window?: BookingWindow;
  // Booking and waitlist pickers asked with include_conflicts: this package
  // would end before a class already on it. Picking it opens the admin's
  // swap / extend choice.
  window_conflict?: boolean;
  // Booking pickers only: credits ONE booking of the picked class takes on
  // this package (the product's per-class cost, 1 by default). The server
  // already leaves out packages whose free credits are below it. Absent from
  // an older server, which reads as 1.
  credit_cost?: number;
  // ── Voided sale ("Void" / "Void Transaksi") ──────────────────────────────
  // A voided sale is not a sale anywhere: it is kept and marked, and every
  // revenue figure drops it. is_valid is false alongside voided_at because
  // every booking guard reads that one, but only voided_at means "this never
  // happened". A legacy row deleted before this feature has is_valid false
  // and voided_at null, and still counts as revenue (no backfill).
  // All absent on an older server.
  voided_at?: string | null;
  voided_by_name?: string | null;
  // A VoidReason, or UPGRADE_CANCELLED_VOID_REASON (shared/utils) on the new
  // half of a cancelled upgrade, which the void dialog never offers.
  void_reason?: VoidReason | string | null;
  void_note?: string | null;
  /**
   * Detail fetch only, and only while the package is NOT yet voided: the
   * same rules the write enforces, so the card can grey the button and say
   * why. `ok` false carries the first failing `reason`. `used_sessions`
   * counts classes already taught on this sale (co-sharers included), which
   * become complimentary. `voucher_question` says which question the modal
   * must ask about the discount voucher spent here, or null for none.
   */
  /** See CancelUpgradeEligibility. Absent on an older server: treated as "hidden". */
  cancel_upgrade_eligibility?: CancelUpgradeEligibility | null;
  void_eligibility?: {
    ok: boolean;
    reason: VoidIneligibleReason | null;
    /** The sale is from an earlier month: a Manager may still void it, and
     *  the confirm says that month's figures will change. Absent on an older
     *  server. */
    past_month?: boolean;
    used_sessions: number;
    voucher_question: "loyalty" | "referral" | null;
    /** The sale converted a referral and a reward from it was already used:
     *  the void proceeds, the referral stays converted. False when the void
     *  reopens the referral. Absent on an older server. */
    referral_kept?: boolean;
  } | null;
}

/** Why the void button is not pressable. Each has a void_hint.* sentence. */
export type VoidIneligibleReason =
  | "past_month"
  | "reward"
  | "upgraded"
  | "has_bookings";

/**
 * Why Cancel Upgrade is not pressable on the new half of an upgrade. The
 * first two hide the button (nothing could ever make it apply); the rest
 * disable it with a cancel_upgrade_hint.* sentence.
 */
export type CancelUpgradeIneligibleReason =
  | "not_upgrade"
  | "membership"
  | "chained"
  | "used_sessions"
  | "has_bookings"
  | "credits_out_of_range"
  | "window"
  | "sharing_capacity"
  | "past_month";

/**
 * Detail fetch only, and only for the new half of an ACTIVE upgrade that is
 * not voided (null otherwise, which reads as "no such action here"): the
 * same rules the write enforces, so the card can show, hide, disable and
 * explain the Cancel Upgrade button. The write stays the authority.
 */
export interface CancelUpgradeEligibility {
  ok: boolean;
  reason: CancelUpgradeIneligibleReason | null;
  past_month: boolean;
  /** The old package's last valid day has passed: allowed, but the returned credits cannot be used. */
  a_expired: boolean;
  /** The new package's expiry was edited by hand after the upgrade; it does not carry back to the old one. */
  b_expiry_edited: boolean;
  referral_reopens: boolean;
  referral_kept: boolean;
  /** The fee that leaves revenue and has to be refunded by hand. */
  fee: number;
  /** The old package's tier name, for the confirm. */
  previous_package_name: string;
  credits_returning: number;
  bookings_returning: number;
  used_sessions: number;
  new_bookings: number;
  window: { className: string; clientName: string; occurrence: string } | null;
}

export interface BookingWindow {
  start: string;
  end: string | null;
  anchor: string | null;
  // The latest class already on a first-class package whose window can
  // still move (Jakarta YYYY-MM-DD). An older server omits it.
  last?: string | null;
}

// A booking, waitlist join or undo that would end a first-class package
// before a class already on it (the server's refusal data). The admin picks
// swap (cancel those bookings and go on) or extend (keep and lock the
// current end date). Mirrors spott-server's WindowConflict.
export interface WindowConflict {
  code: "package_window_conflict";
  customer_package_id: string;
  package_name: string;
  current_end: string | null;
  new_end: string;
  stranded: {
    id: string;
    status: string;
    schedule_id: string;
    occurrence_timestamp: string;
    class_name: string;
    // Who the booking is for (a co-sharer on a shared package). An older
    // server omits it.
    client_name?: string | null;
  }[];
  can_swap: boolean;
}

export type WindowConflictChoice = "swap" | "extend";

// Bookings after a new package end date, listed by the server when an end
// date edit would leave them outside it; the admin may confirm cancelling
// them in the same save.
export interface LaterBooking {
  id: string;
  status: string;
  class_name: string;
  occurrence_timestamp: string;
  client_name?: string | null;
}

// One additional client granted access to a purchased package. Revoked rows
// are returned too (deleted_at set) because the same payload feeds both the
// active member list and the sharing history.
export interface CustomerPackageMember {
  id: string;
  customer_package_id: string;
  user_id: string;
  created_at: string;
  created_by_id: string | null;
  deleted_at: string | null;
  deleted_by_id: string | null;
  customer?: { user?: { full_name?: string | null } | null } | null;
  // Flattened server-side: the member's own name, and the staff who granted or
  // revoked. created_by_id/deleted_by_id are FK-less audit columns, so the
  // server resolves the names rather than the client joining.
  full_name?: string | null;
  created_by_name?: string | null;
  deleted_by_name?: string | null;
  // Detail fetch only: false when this co-sharer is deactivated at the
  // studio (they keep their slot but can't use the package).
  active?: boolean;
}

// Studio-configured label for how a customer paid (e.g. "EDC BCA", "Bank
// Transfer") — see manual_payment_method in schema.prisma.
export interface PaymentMethod {
  id: string;
  name: string;
}

// The data shape shared by the Invoice and Receipt PDFs — assembled
// client-side from whichever purchase row (SalesPackagesRow or
// CustomerPackage) is already loaded, no extra fetch needed.
// `transactionNumber` is formatted with a different prefix per document kind
// (see formatTransactionNumber in shared/utils/transactionNumber.ts) so the
// Invoice and Receipt for one sale print as a visibly matched pair.
export interface PurchaseReceipt {
  transactionNumber: number | null;
  businessName: string;
  businessAddress: string;
  customerName: string;
  // A user phone may be null (non-teaching staff); the receipt formats it
  // through the null-safe formatPhone.
  customerPhone: string | null;
  customerEmail: string | null;
  itemName: string;
  price: number;
  /**
   * On an upgrade: the new package's FULL value as stored on the row
   * (purchase_price), so the document can show what was credited for the old
   * package. Deliberately the stored figure and not the tier's current
   * catalogue price: both it and `price` were computed under the same tax
   * settings at upgrade time, so the two always reconcile. Reading the live
   * catalogue instead would make an old document stop footing the moment
   * someone edits that tier's price.
   */
  upgradeFullPrice?: number | null;
  /**
   * On an upgrade: the name of the package that was traded in, printed on the
   * credit row. The line item names the NEW package and the credit row names
   * the OLD one, so each appears once, in the row that is about it.
   */
  upgradeFromName?: string | null;
  /**
   * On an upgrade: the OLD package's value the fee was priced against, as
   * stored at upgrade time (same tax basis as upgradeFullPrice). Only matters
   * when it exceeds upgradeFullPrice: the credit row then shows it in full,
   * the Total carries an asterisk, and a note states the difference that is
   * not refunded. Null on upgrades recorded before it was stored.
   */
  upgradePreviousPrice?: number | null;
  purchasedAt: string;
  paymentMethodName: string | null;
  // Null when no discount applied — the PDF renders no discount row in that
  // case rather than a zero one. `price` above stays the final,
  // already-discounted amount actually charged (unchanged meaning), so
  // `originalPrice` is only needed to show the pre-discount line-item price.
  // discountSource names the row (Diskon (CODE) / Loyalty / Referral);
  // discountCode is set only for a code.
  discountSource: DiscountSource | null;
  discountCode: string | null;
  // A loyalty/referral reward's percentage, printed beside its label:
  // "Diskon Loyalty (20%)". Null for a fixed amount or a code.
  discountPercent: number | null;
  discountAmount: number | null;
  originalPrice: number | null;
  // From the purchase's OWN tax snapshot (never the business's current
  // settings) — a later settings change never rewrites this document.
  // taxEnabled false covers every purchase made before this feature existed.
  taxEnabled: boolean;
  taxMode: TaxMode | null;
  taxRate: number | null;
  taxLabel: string | null;
  // Printed alongside businessName/businessAddress when set — same
  // "official paperwork" bucket as legal_name.
  npwp: string | null;
  /**
   * Set only once the sale has been voided, in which case both documents are
   * stamped VOID and state the date. Optional so the two readers that build a
   * receipt for a live sale need not mention it at all; a voided sale keeps
   * its documents downloadable precisely so the studio can show WHY the
   * number no longer counts.
   */
  voidedAt?: string | null;
}

export type CustomerPackageDto = {
  previous_customer_package?: CustomerPackage;
  new_customer_package?: CustomerPackage;
} & CustomerPackage;

// One row per MANUAL edit of a package's remaining credits (the admin
// edit-credits modal), plus the two system shapes `reason` names: an import's
// carried-over balance, and a credit returned (or taken back) for a class
// booked on the package this one was upgraded from. Other booking-driven
// changes are not recorded.
export interface CustomerPackageCreditLog {
  id: string;
  customer_package_id: string;
  // Null only on an upgraded_package_booking row nobody with an account
  // caused (rendered "Sistem").
  actor_id: string | null;
  actor: { full_name: string } | null;
  old_remaining_credits: number;
  new_remaining_credits: number;
  reason?: CreditLogReason | null;
  created_at: string;
}

// Audit trail for MANUAL payment-method edits (see
// customer_package_payment_method_log in schema.prisma). Names are already
// flattened server-side (getPaymentMethodLogsByCustomerPackageId) — "-" on
// either side means no payment method was recorded at that point.
export interface CustomerPackagePaymentMethodLog {
  id: string;
  customer_package_id: string;
  actor_id: string;
  actor: { full_name: string };
  old_payment_method_id: string | null;
  new_payment_method_id: string | null;
  old_payment_method_name: string | null;
  new_payment_method_name: string | null;
  created_at: string;
}

// Audit trail for MANUAL edits to a customer package's expiry date (see
// customer_package_expiry_date_log in schema.prisma) — same internal-
// controls purpose as the payment-method log, closing the same gap for the
// other manually-editable field.
export interface CustomerPackageExpiryDateLog {
  id: string;
  customer_package_id: string;
  // Null for rows the validity-start recompute wrote (rendered "Sistem").
  actor_id: string | null;
  actor: { full_name: string } | null;
  old_expiry_date: string | null;
  new_expiry_date: string | null;
  // ExpiryLogReason for system rows; null for a manual admin edit.
  reason?: ExpiryLogReason | null;
  created_at: string;
}

// Audit trail for voiding a customer package (and undoing that). See
// customer_package_status_log in schema.prisma. Only ever has entries for
// packages that went through the void action; a package that's inactive for
// another reason (expired, no more sessions) has none.
export interface CustomerPackageStatusLog {
  id: string;
  customer_package_id: string;
  actor_id: string;
  actor: { full_name: string };
  old_is_valid: boolean;
  new_is_valid: boolean;
  created_at: string;
  /** A VoidReason on a void; UPGRADED_STATUS_REASON on the old half of an
   *  upgrade; UPGRADE_CANCELLED_VOID_REASON on both halves when the upgrade
   *  was cancelled (shared/utils). */
  reason?: VoidReason | string | null;
  /** The void's free text, or the OTHER tier's name on the upgrade rows. */
  note?: string | null;
}

export interface BookingDto {
  id: string;
  schedule_id: string;
  customer_id?: string;
  booking_status: string;
  // Prior status before the last transition; null for bookings created before
  // the previous_status column existed. The undo button mirrors the server
  // guard: undoable only when previous_status === Confirmed.
  previous_status?: string | null;
  // False when the credit-source package was invalidated (upgraded away, or
  // a loyalty claimed-credit claw-back). Undo from Cancelled/Refunded would
  // re-take a credit from it, so the undo button disables on those rows.
  package_is_valid?: boolean;
  // The booking's own package is a membership: no credit moves on any
  // status change, and it can't be refunded (the server refuses).
  package_unlimited?: boolean;
  // The booking's OWN sale was voided, so this class is a COMP: it still
  // counts as a session and an attendee, it simply earned nothing. The server
  // freezes every status change on such a row (no credit may return onto a
  // voided sale), so every action button disables and the row is unselectable
  // for batch actions. Absent on an older server, which reads as not voided.
  package_voided?: boolean;
  // The package's free credits (remaining minus held), null for an unlimited
  // package. Undo from Cancelled/Refunded takes one again, so the undo button
  // disables at zero, mirroring the server. Absent on an older server.
  package_available_credits?: number | null;
  // The booking's package was upgraded since: a refund returns the credit to
  // the client's current package (credit_package_name, which is the booking's
  // own package otherwise), an undo of the refund takes it back from there,
  // and the row can't be restored to Confirmed. credit_package_valid is false
  // when that current package was deleted (refund and its undo refused).
  credit_package_upgraded?: boolean;
  credit_package_name?: string;
  credit_package_valid?: boolean;
  // A Cancelled row whose package ends before its class: the server refuses
  // to undo the cancel, so the undo button disables. Absent on an older server.
  package_ends_before_class?: boolean;
  // Per-schedule list only: false when the client is deactivated at the
  // studio, so the row is read-only.
  customer_active?: boolean;
  // ISO-8601 UTC string from the backend. Wrap with tzDate(...) for display.
  booking_date: string;
  customer_name?: string;
  // E.164 phone of the booked customer. Only the per-schedule bookings list
  // returns it (absent from the customer/package booking lists).
  customer_phone?: string | null;
  staff_name?: string;
  customer_package_name: string;
  customer_package_id: string;
  booking_ref: string | null;
  // Credits this booking took (its snapshot at creation): what an undo
  // re-takes or a refund returns. Absent from an older server (= 1).
  credit_cost?: number;
  cancel_date: string;
  class_name?: string;
  schedule_date?: string;
  // True when the linked schedule was deleted — the client detail page shows a
  // "schedule removed" note instead of linking to a dead schedule page.
  is_schedule_deleted?: boolean;
}

// Per-booking audit trail entry (booking_v2_activity_log). old_status null
// means booking creation. actor_name is server-flattened ("Sistem" for a
// system/cron action — auto-complete, waitlist sweep, waitlist
// auto-promotion with no human actor). Render old_status/new_status via
// getBookingStatusLabel() (shared/utils/index.ts) — never raw, since the
// BookingStatus enum stores legacy DB strings on its Cancelled/NoShow
// members ("refund"/"cancelled_without_refund").
export interface BookingActivityLog {
  id: string;
  booking_id: string;
  old_status: string | null;
  new_status: string;
  actor_name: string;
  created_at: string;
}

// One row per class an instructor taught (or has coming up) — the instructor
// detail page's Class History tab, backed by GET /v2/booking/instructor/:id.
// CLASS-focused, not booking-focused: unlike BookingDto above, attendees is
// a count (how many bookings landed on this occurrence), not a list — the
// row links straight to the schedule detail page, it doesn't carry
// per-attendee detail to display inline.
export interface InstructorSessionRow {
  // schedule_id + occurrence — unique row key (class_name + start_time can
  // collide across schedules).
  session_key: string;
  schedule_id: string;
  class_name: string;
  class_type: string;
  // ISO-8601 UTC string from the backend. Wrap with tzDate(...) for display.
  start_time: string;
  attendees: number;
  // True when the linked schedule was deleted — same "no detail page to
  // open" guard CustomerBookingTable uses before navigating.
  is_schedule_deleted: boolean;
}
